/**
 * Plain-word labels for internal codes (docs/UX-SPEC.md 4.1, docs/UI-KIT.md glossary).
 * Section names come from the prototype's SECTIONS map (Business_Sense_v10_346.html), reworded.
 * Codes (A, L117, ball) never appear as the main label; show them only in a small tooltip.
 */
export const SECTION_LABEL: Record<string, string> = {
  A: 'Paying customers',
  P: 'Trial customers',
  B: 'Hot: replied this week',
  C: 'Waiting on them',
  D: 'Proposal sent',
  E: 'Recently contacted',
  F: 'Gone quiet',
  O: 'New: not contacted yet',
  G: 'Closed: do not chase',
};
export const SECTION_ORDER = ['A', 'P', 'B', 'C', 'D', 'E', 'F', 'O', 'G'] as const;
export const sectionLabel = (s: string): string => SECTION_LABEL[s] ?? s;

export const BALL_LABEL: Record<string, string> = {
  them: 'Their turn to reply',
  us: 'Your turn to reply',
  shared: 'Both sides have a step',
};
export const ballLabel = (b: string): string => BALL_LABEL[b] ?? b;

export const REASON_LABEL: Record<string, string> = {
  watch_due: 'Next contact date reached',
  commitment_due: 'A promise is due',
  unacked_inbound: 'They wrote and have not had a reply',
  renewal: 'Renewal coming up',
  overdue: 'Overdue',
};
export const reasonLabel = (r: string): string => REASON_LABEL[r] ?? r;

export function areaLabel(key: string): string {
  if (key === 'seo') return 'SEO';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

export const messagesLabel = (n: number): string => `${n} message${n === 1 ? '' : 's'}`;

/** One-word stage names for chart axes. */
export const SECTION_SHORT: Record<string, string> = {
  A: 'Paying', P: 'Trial', B: 'Hot', C: 'Waiting', D: 'Proposal', E: 'Recent', F: 'Quiet', O: 'New', G: 'Closed',
};

/**
 * Selling-rule text is stored verbatim (the AI engines read it word for word). On screen, internal terms are
 * swapped for plain words. Display only: the stored rule is never changed. Order matters (longer phrases first).
 */
const RULE_PLAIN: [RegExp, string][] = [
  [/\bthe ball is with them\b/gi, 'it is their turn to reply'],
  [/\bthe ball with the sender\b/gi, 'the next step with the sender'],
  [/\bAn outbound sent\b/g, 'A message you sent'],
  [/\ban outbound sent\b/g, 'a message you sent'],
  [/\bAn outbound\b/g, 'A message you sent'],
  [/\ban outbound\b/g, 'a message you sent'],
  [/\bAn inbound\b/g, 'A message from them'],
  [/\ban inbound\b/g, 'a message from them'],
  [/\bwatchSetOn\b/g, 'the day the next contact date was set'],
  [/\bmemoryNotes\b/g, 'the notes about this person'],
  [/\bcurrentStatus\b/g, '"where we are"'],
  [/\bcurrentNext\b/g, '"next step"'],
  [/\bopenTickets\b/g, 'the list of open problems'],
  [/\bwaiting_note\b/g, 'the waiting list'],
  [/\bwhat_moves_it\b/g, 'what would move it'],
  [/\boutbound touches\b/gi, 'messages you send'],
  [/\bproduct touches\b/gi, 'product messages'],
  [/\btouches\b/gi, 'messages sent'],
  [/\ba touch\b/gi, 'a message'],
  [/\btouch\b/gi, 'message'],
  [/\boutbound\b/gi, 'message you sent'],
  [/\binbound\b/gi, 'message from them'],
  [/\bwatch dates?\b/gi, 'next contact date'],
  [/\bwatch (decision|change|proposal)\b/gi, 'next-contact-date $1'],
  [/\b(clearing|clear) a watch\b/gi, '$1 the next contact date'],
  [/\bthe watch\b/gi, 'the next contact date'],
  [/\bWatch Stability\b/g, 'Next contact date stability'],
  [/\bC, D, or B stage row\b/g, 'live person (waiting, proposal or hot)'],
  [/\b(per|each|a|the|this|that|any) row's\b/gi, "$1 person's"],
  [/\b(per|each|a|the|this|that|any|live|due) rows?\b/gi, '$1 person'],
  [/\brows\b/gi, 'people'],
  [/\brow\b/gi, 'person'],
  [/\bICP\b/g, 'ideal-customer'],
];
const STAGE_WORD: Record<string, string> = { A: 'paying', P: 'trial', B: 'hot', C: 'waiting', D: 'proposal', E: 'recent', F: 'quiet', O: 'new', G: 'closed' };

export function plainRuleText(text: string): string {
  let out = text.replace(/\b([A-GOP])-stage row\b/g, (_m, s: string) => `person at the ${STAGE_WORD[s] ?? s} stage`);
  for (const [re, to] of RULE_PLAIN) out = out.replace(re, to);
  return out;
}
